import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import { getParts } from '../api/partsAPI';
import { getPaginatedSuppliers } from '../api/supplierAPI';
import { getRotableTransactions, updateRotableTransaction } from '../api/rotable-transactionsAPI';
import { getAll } from '../api';
import { formatIntDate } from '../utils/dateUtils';
import { createStockToRepairStatusMap } from '../shared/rotableStatusMaps';
import { getUsersById } from '../api/usersAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import AddPartModal from './parts/AddPartModal';
import RotableTransactionEditModal from './RotableTransactionEditModal';
import DocumentDataTable from './DocumentDataTable';
import { deleteUploadedFile } from '../api/uploadAPI';

const today = new Date().toISOString().split('T')[0];

const DEFAULT_ROTABLE = {
  serialNumber: '',
  partId: null,
  stockItemId: null,
  statusId: 1,
  repairStatusId: 1,
  groups: [],
  supplierId: null,
  zone: '',
  aisle: '',
  bay: '',
  shelf: '',
  componentTypeId: 1,
  estimatedRepairTime: '',
  numberOfTurns: '',
  manufactureDate: today,
  arrangementNumber: '',
  warrantyStart: today,
  warrantyEnd: today,
  warrantyMonths: 0,
  warrantyUsage: 0,
  notes: '',
  usage: '',
  files: [],
};

const DEFAULT_PART_CATEGORY_ID = 21;

const RotableModal = ({ show, onClose, onSave, initialValues }) => {
  const [rotable, setRotable] = useState(DEFAULT_ROTABLE);
  const [selectedPartOption, setSelectedPartOption] = useState(null);
  const [partSearchText, setPartSearchText] = useState('');
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [addPartInitialValues, setAddPartInitialValues] = useState({});
  const [partSelectRefreshKey, setPartSelectRefreshKey] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [stockStatusOptions, setStockStatusOptions] = useState([]);
  const [repairStatusOptions, setRepairStatusOptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionUsers, setTransactionUsers] = useState({});
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editTransactionModalOpen, setEditTransactionModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const hasInitializedRepairStatus = useRef(false);
  const userProfile = useUserProfile();

  const PARTS_PAGE_SIZE = 25;
  const addNewPartOption = useMemo(
    () => ({ value: 'add-new', label: '+ Add New Part', isAddNew: true }),
    [],
  );
  const isEditMode = Boolean(initialValues?.id);
  const canEditTransactions = userProfile?.privileges?.includes('rotables:update');

  const normalizeStatusName = useCallback((value) => String(value || '').trim().toLowerCase(), []);
  const normalizeGroupIds = useCallback((groups) => {
    if (groups === null || groups === undefined) return [];
    const coerceGroupId = (group) => {
      if (group === null || group === undefined) return null;
      if (typeof group === 'object') {
        return group.id ?? group.groupId ?? group.value ?? null;
      }
      return group;
    };

    if (Array.isArray(groups)) {
      return groups
        .map(coerceGroupId)
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => (typeof value === 'string' ? value.trim() : value));
    }

    if (typeof groups === 'string') {
      const trimmed = groups.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        return normalizeGroupIds(parsed);
      } catch (err) {
        return [];
      }
    }

    if (typeof groups === 'object') {
      if (Array.isArray(groups.groups)) return normalizeGroupIds(groups.groups);
      if (Array.isArray(groups.group)) return normalizeGroupIds(groups.group);
    }
    return [];
  }, []);
  const stockToRepairStatusMap = useMemo(
    () => createStockToRepairStatusMap(normalizeStatusName),
    [normalizeStatusName],
  );

  const selectedStockStatusName = useMemo(() => {
    const option = stockStatusOptions.find(
      (statusOption) => String(statusOption.value) === String(rotable.statusId),
    );
    return option?.raw?.name || rotable?.status?.name || '';
  }, [rotable.statusId, rotable.status?.name, stockStatusOptions]);

  const allowedRepairStatusNames = useMemo(() => {
    const normalized = normalizeStatusName(selectedStockStatusName);
    const allowed = stockToRepairStatusMap[normalized];
    return allowed ? new Set(allowed) : null;
  }, [normalizeStatusName, selectedStockStatusName, stockToRepairStatusMap]);

  const filteredRepairStatusOptions = useMemo(() => {
    if (!allowedRepairStatusNames) {
      return repairStatusOptions;
    }
    return repairStatusOptions.filter((option) => {
      const optionName = normalizeStatusName(option?.raw?.name || option?.label);
      return allowedRepairStatusNames.has(optionName);
    });
  }, [allowedRepairStatusNames, normalizeStatusName, repairStatusOptions]);

  const formatPartOptionLabel = useCallback((partNumber, description) => {
    const safeNumber = typeof partNumber === 'string' || typeof partNumber === 'number'
      ? String(partNumber).trim()
      : '';
    const safeDescription = typeof description === 'string' ? description.trim() : '';
    return [safeNumber, safeDescription].filter(Boolean).join(' – ');
  }, []);

  const handlePartSearch = useCallback(
    async (inputValue = '', { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      setPartSearchText(trimmed);
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [addNewPartOption], hasMore: false };
      }

      try {
        const resp = await getParts({
          page: pageNumber,
          pageSize: PARTS_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((item) => ({
          value: item.id,
          label: formatPartOptionLabel(item.partNumber, item.description) || `Part ${item.id}`,
          raw: item,
        }));
        const optionsWithAddNew = pageNumber === 1 ? [addNewPartOption, ...options] : options;
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : typeof resp?.meta?.total === 'number'
                ? resp.meta.total
                : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * PARTS_PAGE_SIZE < totalCount
              : items.length === PARTS_PAGE_SIZE;

        return {
          options: optionsWithAddNew,
          totalCount,
          pageSize: PARTS_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching parts:', err);
        return { options: [addNewPartOption], hasMore: false };
      }
    },
    [addNewPartOption, formatPartOptionLabel],
  );

  const handleSupplierSearch = useCallback(async (inputValue = '', { page = 1 } = {}) => {
    try {
      const trimmedSearch = typeof inputValue === 'string' ? inputValue.trim() : '';
      const initialPageSize = 10;
      const lazyPageSize = 5;
      const pageSize = page === 1 ? initialPageSize : lazyPageSize;
      const apiPage = page === 1 ? 1 : page + 1;
      const params = { page: apiPage, pageSize };

      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const result = await getPaginatedSuppliers(params);
      const items = result.data || result;
      const options = items.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
        raw: supplier,
      }));

      const total =
        typeof result.total === 'number'
          ? result.total
          : typeof result.count === 'number'
            ? result.count
            : undefined;
      const loadedCount =
        page === 1
          ? initialPageSize
          : initialPageSize + (page - 1) * lazyPageSize;
      const hasMore =
        typeof total === 'number'
          ? loadedCount < total
          : options.length === pageSize;

      return { options, hasMore };
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      return { options: [], hasMore: false };
    }
  }, []);

  const buildSupplierOption = useCallback((supplierId, supplier) => {
    if (!supplierId) return null;
    const label = supplier?.name || `Supplier ${supplierId}`;
    return {
      value: supplierId,
      label,
      raw: supplier ? { ...supplier, id: supplierId, name: label } : { id: supplierId, name: label },
    };
  }, []);

  const syncSupplierFromPart = useCallback(
    (part, setFormDataFn) => {
      const supplierId = part?.supplierId ?? part?.supplier?.id ?? null;
      const supplier = part?.supplier ?? null;
      if (setFormDataFn) {
        setFormDataFn((prev) => ({
          ...prev,
          supplierId,
          supplier,
        }));
      }
      setSelectedSupplier(buildSupplierOption(supplierId, supplier));
    },
    [buildSupplierOption],
  );

  useEffect(() => {
    if (!show) return;
    let isMounted = true;

    const loadLookups = async () => {
      try {
        const [stockStatusResponse, repairStatusResponse] = await Promise.all([
          getAll('stock-item-status'),
          getAll('repair-status'),
        ]);
        if (!isMounted) return;
        const stockItems = Array.isArray(stockStatusResponse?.data)
          ? stockStatusResponse.data
          : Array.isArray(stockStatusResponse)
            ? stockStatusResponse
            : [];
        const repairItems = Array.isArray(repairStatusResponse?.data)
          ? repairStatusResponse.data
          : Array.isArray(repairStatusResponse)
            ? repairStatusResponse
            : [];
        const serialisedOnlyStockItems = stockItems.filter(
          (item) => Number(item?.serialisedOnly) === 1,
        );
        setStockStatusOptions(
          serialisedOnlyStockItems.map((item) => ({
            value: item.id,
            label: item.name,
            raw: item,
          })),
        );
        setRepairStatusOptions(
          repairItems.map((item) => ({
            value: item.id,
            label: item.name,
            raw: item,
          })),
        );
      } catch (err) {
        console.error('Error fetching status lookups:', err);
      }
    };

    loadLookups();
    return () => {
      isMounted = false;
    };
  }, [show]);

  useEffect(() => {
    if (!show) {
      hasInitializedRepairStatus.current = false;
      return;
    }
    if (
      isEditMode
      || hasInitializedRepairStatus.current
      || !filteredRepairStatusOptions.length
    ) {
      return;
    }
    const hasInitialRepairStatus = initialValues?.repairStatusId != null;
    if (!hasInitialRepairStatus) {
      const defaultOption = filteredRepairStatusOptions.find(
        (option) => Number(option.value) === 1,
      );
      setRotable((prev) => ({
        ...prev,
        repairStatusId: defaultOption?.value ?? filteredRepairStatusOptions[0]?.value ?? null,
      }));
    }
    hasInitializedRepairStatus.current = true;
  }, [filteredRepairStatusOptions, initialValues?.repairStatusId, isEditMode, show]);

  useEffect(() => {
    if (!show || !initialValues?.id) {
      setTransactions([]);
      return;
    }
    let isMounted = true;
    setTransactionsLoading(true);
    getRotableTransactions({ rotableId: initialValues.id })
      .then((data) => {
        if (!isMounted) return;
        setTransactions(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching rotable transactions:', err);
        if (isMounted) {
          setTransactions([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setTransactionsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialValues?.id, show]);

  useEffect(() => {
    if (!transactions.length) return;
    const userIds = [...new Set(transactions.map((transaction) => transaction.createdBy).filter(Boolean))];
    const missingIds = userIds.filter((id) => !transactionUsers[id]);
    if (!missingIds.length) return;
    let isActive = true;
    Promise.all(
      missingIds.map((id) =>
        getUsersById(id)
          .then((user) => ({ id, user }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (!isActive) return;
      const updates = results.reduce((acc, result) => {
        if (result?.id && result.user) {
          acc[result.id] = result.user;
        }
        return acc;
      }, {});
      if (Object.keys(updates).length) {
        setTransactionUsers((prev) => ({ ...prev, ...updates }));
      }
    });

    return () => {
      isActive = false;
    };
  }, [transactionUsers, transactions]);

  useEffect(() => {
    const normalizedGroups = normalizeGroupIds(initialValues?.groups ?? initialValues?.groupIds);
    setRotable({
      ...DEFAULT_ROTABLE,
      ...(initialValues || {}),
      groups: normalizedGroups,
    });
  }, [initialValues, normalizeGroupIds, show]);

  useEffect(() => {
    if (show) {
      setActiveTab('general');
    }
  }, [show]);

  const transactionsWithUsers = useMemo(
    () =>
      transactions.map((transaction) => ({
        ...transaction,
        createdByUser: transactionUsers[transaction.createdBy] || null,
      })),
    [transactionUsers, transactions],
  );

  useEffect(() => {
    if (!show) return;
    const part = initialValues?.part;
    if (part?.id) {
      setSelectedPartOption({
        value: part.id,
        label: formatPartOptionLabel(part.partNumber, part.description) || `Part ${part.id}`,
        raw: part,
      });
      return;
    }

    if (initialValues?.partId) {
      setSelectedPartOption((prev) =>
        prev?.value === initialValues.partId ? prev : null,
      );
    } else {
      setSelectedPartOption(null);
    }
  }, [formatPartOptionLabel, initialValues, show]);

  useEffect(() => {
    if (!show) return;
    const supplier = initialValues?.supplier || null;
    setSelectedSupplier(
      supplier?.id
        ? {
          value: supplier.id,
          label: supplier.name || 'Supplier',
          raw: supplier,
        }
        : null,
    );
  }, [initialValues, show]);

  useEffect(() => {
    if (!allowedRepairStatusNames || !rotable.repairStatusId) {
      return;
    }
    const currentOption = repairStatusOptions.find(
      (option) => String(option.value) === String(rotable.repairStatusId),
    );
    const currentName = normalizeStatusName(currentOption?.raw?.name || currentOption?.label);
    if (currentName && !allowedRepairStatusNames.has(currentName)) {
      setRotable((prev) => ({ ...prev, repairStatusId: null }));
    }
  }, [allowedRepairStatusNames, normalizeStatusName, repairStatusOptions, rotable.repairStatusId]);

  const rotableGeneralFields = useMemo(
    () => [
      {
        field: 'serialNumber',
        label: 'Serial Number',
        type: 'text',
        size: '4',
        required: true,
        maxLength: 45,
        disabled: isEditMode,
      },
      {
        field: 'partId',
        label: 'Part Number',
        type: 'paginated-select',
        size: '4',
        required: true,
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        minSearchLength: 2,
        disabled: isEditMode,
        onChange: (option, setFormData, field, setSelectedOptionFn) => {
          if (option?.isAddNew) {
            if (setSelectedOptionFn) {
              setSelectedOptionFn(null);
            }
            setAddPartInitialValues((prev) => ({
              ...prev,
              partNumber: partSearchText,
              categoryId: DEFAULT_PART_CATEGORY_ID,
            }));
            setShowAddPartModal(true);
            return;
          }

          if (setSelectedOptionFn) {
            setSelectedOptionFn(option ?? null);
          }

          const selectedPart = option?.raw ?? null;
          setFormData((prev) => ({
            ...prev,
            [field]: option?.value ?? null,
            partId: option?.value ?? null,
            part: selectedPart,
          }));
          syncSupplierFromPart(selectedPart, setFormData);
        },
      },
      {
        field: 'stockItemId',
        label: 'Stock Item',
        type: 'select',
        size: '4',
        api: 'stock-items',
        displayField: (item) => `${item.materialNumber || 'N/A'} - ${item.description || ''}`,
        disabled: isEditMode,
      },
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: '4',
        options: stockStatusOptions,
        required: true,
        disabled: isEditMode,
      },
      {
        field: 'repairStatusId',
        label: 'Repair Status',
        type: 'select',
        size: '4',
        options: filteredRepairStatusOptions,
        required: true,
        disabled: isEditMode,
      },
      {
        field: 'componentTypeId',
        label: 'Component Type',
        type: 'select',
        size: '4',
        api: 'component-type',
        displayField: 'name',
        onChange: (option, setFormData, field) => {
          setFormData((prev) => ({
            ...prev,
            [field]: option?.value ?? null,
            componentType: option?.raw ?? null,
          }));
        },
      },
      { field: 'locationId', label: 'Default Location', type: 'select', api: 'locations', size: '4' },
      {
        field: 'supplierId',
        label: 'Original Supplier',
        type: 'paginated-select',
        size: '4',
        loadOptions: handleSupplierSearch,
        selectedOption: selectedSupplier,
        setSelectedOption: setSelectedSupplier,
        isClearable: true,
        minSearchLength: 2,
      },
      { field: 'arrangementNumber', label: 'Arrangement Number', type: 'text', size: '4', maxLength: 25 },
      {
        field: 'groups',
        label: 'Compatible Models',
        type: 'multi-select',
        size: '12',
        api: 'groups',
        displayField: (group) => group?.groupName ?? group?.name ?? '',
        formatOptionLabel: (option, { context } = {}) => {
          const group = option?.raw ?? {};
          const groupName = group?.groupName ?? option?.label ?? '';
          if (context === 'value') {
            return groupName;
          }
          const manufacturerName = group?.manufacturer?.manufacturer ?? '';
          const className = group?.class?.className ?? '';
          return [manufacturerName, groupName, className].filter(Boolean).join(' ');
        },
      },
      // { field: 'GeneralSpacer_1', label: '', type: 'empty', api: 'locations', size: '4', },
      /*{ field: 'zone', label: 'Zone', type: 'text', size: '3', maxLength: 25 },
      { field: 'aisle', label: 'Aisle', type: 'text', size: '3', maxLength: 25 },
      { field: 'bay', label: 'Bay', type: 'text', size: '3', maxLength: 25 },
      { field: 'shelf', label: 'Shelf', type: 'text', size: '3', maxLength: 25 },*/
      
      {
        field: 'estimatedRepairTime',
        label: 'Estimated Repair Time (days)',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
        allowZeroValue: true
      },
      {
        field: 'numberOfTurns',
        label: 'Number of Turns',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
        allowZeroValue: true,
        disabled: isEditMode,
      },
      { field: 'manufactureDate', label: 'Manufacture Date', type: 'date', size: '3', noFutureDate: true },
      { field: 'usage', label: 'Current Usage', type: 'number', size: '3', mustBePositiveInteger: true, allowZeroValue: true },
      { field: 'warrantyStart', label: 'Warranty Start', type: 'date', size: '3' },
      { field: 'warrantyMonths', label: 'Warranty Months', type: 'number', size: '3', mustBePositiveInteger: true, allowZeroValue: true },
      { field: 'warrantyUsage', label: 'Warranty Usage', type: 'number', size: '3', mustBePositiveDecimal: true, allowZeroValue: true },
      // { field: 'warrantyEnd', label: 'Warranty End', type: 'date', size: '3', disabled: isEditMode },
      { field: 'notes', label: 'Notes', type: 'textarea', size: '12', lines: '3' },
    ],
    [
      filteredRepairStatusOptions,
      handlePartSearch,
      handleSupplierSearch,
      isEditMode,
      partSearchText,
      selectedPartOption,
      selectedSupplier,
      syncSupplierFromPart,
      stockStatusOptions,
    ],
  );

  const rotableDocumentFields = useMemo(
    () => [
      {
        field: 'files',
        label: 'Documents',
        type: 'files',
        size: '12',
        directory: 'assetfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const rotableValidationFields = useMemo(
    () => [...rotableGeneralFields, ...rotableDocumentFields],
    [rotableGeneralFields, rotableDocumentFields],
  );

  const { errors, setErrors, validate } = useValidator(rotableValidationFields, rotable);
  const { renderField: renderGeneralField } = useFormBuilder(
    rotableGeneralFields,
    rotable,
    setRotable,
    errors,
    {
      refreshKey: partSelectRefreshKey,
    },
  );
  const { renderField: renderDocumentField } = useFormBuilder(
    rotableDocumentFields,
    rotable,
    setRotable,
    errors,
  );

  const handleFileDelete = async (filename, idx) => {
    if (!window.confirm('Delete this file?')) return;
    setRotable((prev) => ({
      ...prev,
      files: (prev.files || []).filter((_, i) => i !== idx),
    }));
    try {
      await deleteUploadedFile(filename, 'assetfiles');
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };

  const handleSave = () => {
    const isValid = validate();
    const dateErrors = {};

    if (rotable.warrantyStart && rotable.warrantyEnd) {
      const start = new Date(rotable.warrantyStart);
      const end = new Date(rotable.warrantyEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
        dateErrors.warrantyEnd = 'Warranty end date cannot be before the start date';
      }
    }

    if (!isValid || Object.keys(dateErrors).length) {
      if (Object.keys(dateErrors).length) {
        setErrors((prev) => ({ ...prev, ...dateErrors }));
      }
      return;
    }

    const { partNumber, ...payload } = rotable;
    payload.groups = normalizeGroupIds(rotable.groups);

    if (isEditMode === true) {
      delete payload.repairStatusId;
      delete payload.statusId
    }
    onSave?.(payload);
    onClose?.();
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditTransactionModalOpen(true);
  };

  const handleTransactionUpdate = async (payload) => {
    if (!editingTransaction?.id) return;
    try {
      const updated = await updateRotableTransaction(editingTransaction.id, payload);
      setTransactions((prev) =>
        prev.map((transaction) => (transaction.id === updated.id ? updated : transaction)),
      );
      setEditingTransaction(updated);
      setEditTransactionModalOpen(false);
    } catch (err) {
      console.error('Unable to update rotable transaction', err);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{initialValues?.id ? 'Edit Rotable' : 'Add Rotable'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body" style={{ height: "700px" }}>
            <ul className="nav nav-tabs mb-3" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('general')}
                >
                  General
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('transactions')}
                >
                  Transactions
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('documents')}
                >
                  Documents
                </button>
              </li>
            </ul>
            <div className="tab-content pt-3">
              <div
                className={`tab-pane fade ${activeTab === 'general' ? 'show active' : ''}`}
                role="tabpanel"
              >
                <div className="row g-3">{rotableGeneralFields.map(renderGeneralField)}</div>
              </div>
              <div
                className={`tab-pane fade ${activeTab === 'transactions' ? 'show active' : ''}`}
                role="tabpanel"
              >
                {initialValues?.id ? (
                  <div className="mt-2">
                    <div
                      className="table-responsive"
                      style={{ height: "580px", overflow: "auto" }}
                    >
                      <table className="table table-sm bordered-table mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Repair Status</th>
                            <th>Asset</th>
                            <th>Task</th>
                            <th>Work Order</th>
                            <th>Usage</th>
                            <th>Notes</th>
                            {canEditTransactions && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {transactionsLoading && (
                            <tr>
                              <td colSpan={canEditTransactions ? 10 : 9} className="text-center py-3">
                                <span className="spinner-border spinner-border-sm" />
                              </td>
                            </tr>
                          )}
                          {!transactionsLoading && transactions.length === 0 && (
                            <tr>
                              <td colSpan={canEditTransactions ? 10 : 9} className="text-center py-3">
                                No transactions recorded.
                              </td>
                            </tr>
                          )}
                          {!transactionsLoading &&
                            transactionsWithUsers.map((transaction) => (
                              <tr key={transaction.id}>
                                <td>{formatIntDate(transaction.createdAt)}</td>
                                <td>{transaction.rotableStatus?.name ?? '—'}</td>
                                <td>{transaction.repairStatus?.name ?? '—'}</td>
                                <td>{transaction.asset?.assetNumber ?? '—'}</td>
                                <td>{transaction.task?.name ?? '—'}</td>
                                <td>{transaction.workOrder?.workOrderNumber ?? '—'}</td>
                                <td>{transaction.usage ?? '—'}</td>
                                <td className="notes-cell">{transaction.notes || '—'}</td>
                                {canEditTransactions && (
                                  <td>
                                    <button
                                      className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                                      onClick={() => handleEditTransaction(transaction)}
                                      aria-label="Edit rotable transaction"
                                    >
                                      <Icon icon="ic:baseline-edit" />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted">
                    Save the rotable to view transactions.
                  </div>
                )}
              </div>
              <div
                className={`tab-pane fade ${activeTab === 'documents' ? 'show active' : ''}`}
                role="tabpanel"
              >
                <div className="row g-3">{rotableDocumentFields.map(renderDocumentField)}</div>
                {Array.isArray(rotable.files) && rotable.files.length > 0 && (
                  <DocumentDataTable files={rotable.files} onDelete={handleFileDelete} />
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
      <AddPartModal
        show={showAddPartModal}
        onClose={() => setShowAddPartModal(false)}
        initialValues={addPartInitialValues}
        onPartCreated={(payload) => {
          const created = payload?.part;
          if (!created?.id) return;
          const nextOption = {
            value: created.id,
            label: formatPartOptionLabel(created.partNumber, created.description) || `Part ${created.id}`,
            raw: created,
          };
          setSelectedPartOption(nextOption);
          setRotable((prev) => ({
            ...prev,
            partId: created.id,
            part: created,
            supplierId: payload?.supplierId ?? created?.supplierId ?? null,
            supplier: payload?.supplier ?? created?.supplier ?? null,
          }));
          syncSupplierFromPart(
            {
              supplierId: payload?.supplierId ?? created?.supplierId ?? null,
              supplier: payload?.supplier ?? created?.supplier ?? null,
            },
            setRotable,
          );
          setShowAddPartModal(false);
          setAddPartInitialValues({});
          setPartSelectRefreshKey((prev) => prev + 1);
        }}
      />
      {editTransactionModalOpen && (
        <RotableTransactionEditModal
          show={editTransactionModalOpen}
          transaction={editingTransaction}
          createdByUser={transactionUsers[editingTransaction?.createdBy] || null}
          onClose={() => {
            setEditTransactionModalOpen(false);
            setEditingTransaction(null);
          }}
          onSave={handleTransactionUpdate}
        />
      )}
    </div>
  );
};

export default RotableModal;
